<template>
    <v-sheet>
        <v-row class="pa-1" :dense="true">
            <v-col cols="6">
                <v-btn-toggle :divided="true" variant="outlined">
                    <v-btn @click="firstOperation">
                        <font-awesome-icon
                            :icon="['fas', 'backward-step']"
                            class="text-primary"
                        />
                    </v-btn>

                    <v-btn @click="previousOperation">
                        <font-awesome-icon
                            :icon="['fas', 'play']"
                            class="text-primary fa-rotate-180"
                        />
                    </v-btn>

                    <v-select
                        v-model="form.OprSeq"
                        :items="operationSelection"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        rounded="0"
                        hide-details
                        style="min-width: 10rem"
                    ></v-select>

                    <v-btn @click="nextOperation">
                        <font-awesome-icon
                            :icon="['fas', 'play']"
                            class="text-primary"
                        />
                    </v-btn>

                    <v-btn @click="lastOperation">
                        <font-awesome-icon
                            :icon="['fas', 'forward-step']"
                            class="text-primary"
                        />
                    </v-btn>
                </v-btn-toggle>
            </v-col>

            <v-col class="d-flex align-center justify-start" cols="3">
                <v-checkbox
                    v-model="form.SubContract"
                    color="primary"
                    density="compact"
                    label="Subcontract"
                    hide-details
                    :readonly="true"
                ></v-checkbox>
            </v-col>
        </v-row>

        <v-row class="pa-1" :dense="true">
            <v-col>
                <div class="section-heading font-weight-bold">Operation</div>

                <v-row :dense="true">
                    <v-col>
                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Opr:
                            </v-col>

                            <v-col cols="4">
                                <v-text-field
                                    v-model="form.OprSeq"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    type="number"
                                    placeholder="0"
                                    hide-details
                                    :readonly="parseInt(job?.JobClosed) !== 0"
                                    @focusout="OprSeqChange"
                                    @keydown.enter="OprSeqChange"
                                    @keydown.tab="OprSeqChange"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Description:
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    v-model="form.OpDesc"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    hide-details
                                    :readonly="true"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Operation:
                            </v-col>

                            <v-col cols="8">
                                <v-select
                                    :items="operationsArray"
                                    item-value="OpCode"
                                    item-title="OpDesc"
                                    v-model="form.OpCode"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    hide-details
                                    :readonly="parseInt(job?.JobClosed) !== 0"
                                    @update:modelValue="OpCodeChange"
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-col>

            <v-divider class="mx-3" :vertical="true" />

            <v-col>
                <div class="section-heading font-weight-bold">
                    Quantities / Dates
                </div>

                <v-row :dense="true">
                    <v-col>
                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Production:
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    v-model="form.ProdQty"
                                    :readonly="true"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    type="number"
                                    placeholder="0.00"
                                    hide-details
                                    suffix="PC"
                                >
                                    <v-tooltip
                                        activator="parent"
                                        location="bottom"
                                    >
                                        This value is based on the prod quantity
                                        of the job and the Qty/Parent values
                                    </v-tooltip>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Scrap:
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    v-model="form.ScrapTotal"
                                    :readonly="true"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    type="number"
                                    placeholder="0.00"
                                    hide-details
                                    suffix="PC"
                                >
                                    <v-tooltip
                                        activator="parent"
                                        location="bottom"
                                    >
                                        This value is based on Scrap value
                                        listed below and the Production value
                                    </v-tooltip>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Run:
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    v-model="form.RunQty"
                                    :readonly="true"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    type="number"
                                    placeholder="0.00"
                                    hide-details
                                    suffix="PC"
                                >
                                    <v-tooltip
                                        activator="parent"
                                        location="bottom"
                                    >
                                        This value is based on the two values
                                        above and is the final quantity that
                                        will be produced
                                    </v-tooltip>
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-col>

                    <v-col>
                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Start:
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    v-model="form.QueStartDate"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    type="date"
                                    hide-details
                                    :readonly="true"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row :dense="true">
                            <v-col
                                class="d-flex justify-end align-center"
                                cols="4"
                            >
                                Due:
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    v-model="form.DueDate"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    type="date"
                                    hide-details
                                    :readonly="true"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-divider class="my-3" />

        <v-row class="bg-red-accent-1 pa-1" :dense="true">
            <v-col cols="10">
                <div class="section-heading font-weight-bold">
                    Quantity Factors
                </div>

                <v-row :dense="true">
                    <v-col class="d-flex align-center justify-end" cols="3">
                        Qty/Parent:
                    </v-col>

                    <v-col
                        cols="6"
                        style="display: flex; align-items: center; gap: 7px"
                    >
                        <v-btn
                            :disabled="parseInt(job?.JobClosed) !== 0"
                            @click="openQtyPerCalculator"
                        >
                            <v-icon
                                icon="mdi:mdi-calculator"
                                size="x-large"
                                class="button"
                            ></v-icon>
                        </v-btn>

                        <v-text-field
                            v-model="form.QtyPer"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            type="number"
                            placeholder="0.00000000"
                            hide-details
                            :readonly="parseInt(job?.JobClosed) !== 0"
                            @focusout="QtyPerChange"
                            @keydown.enter="QtyPerChange"
                            @keydown.tab="QtyPerChange"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-row :dense="true">
                    <v-col class="d-flex align-center justify-end" cols="3">
                        Scrap:
                    </v-col>

                    <v-col cols="4">
                        <v-text-field
                            v-model="form.EstScrap"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            type="number"
                            placeholder="0.00"
                            :suffix="jobPassDate ? '%' : ''"
                            hide-details
                            :readonly="parseInt(job?.JobClosed) !== 0"
                            @focusout="EstScrapChange"
                            @keydown.enter="EstScrapChange"
                            @keydown.tab="EstScrapChange"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="3" v-if="!jobPassDate">
                        <v-radio-group
                            :inline="true"
                            v-model="form.EstScrapType"
                            @update:modelValue="EstScrapTypeChange"
                        >
                            <v-radio label="Qty" value="Q"></v-radio>

                            <v-radio label="%" value="%"></v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-sheet>

    <v-dialog v-model="qtyPerCalculatorDialog">
        <QtyPerCalculator
            @closeQtyPerCalculator="closeQtyPerCalculator"
            @qtyPerCalculatorReceived="qtyPerCalculatorReceived"
        />
    </v-dialog>
</template>

<script>
import { useJobEntryStore } from "../../../../../../stores/ProjectManagement/JobEntry";
import { mapWritableState } from "pinia";
import axios from "axios";
import QtyPerCalculator from "../../../../EngineeringWorkBench/MethodOfManufacturing/QtyPerCalculator.vue";
import moment from "moment";

export default {
    name: "DetailWindow",
    components: {
        QtyPerCalculator,
    },
    computed: {
        ...mapWritableState(useJobEntryStore, [
            "currentOperation",
            "currentAssemblyOperations",
            "currentAssembly",
            "setCurrentOperation",
            "job",
            "refreshAssemblies",
            "setErrorMessage",
            "setTemporaryMessage",
            "fetchJobData",
            "setJob",
            "overlay",
        ]),
        operationSelection() {
            return this.currentAssemblyOperations.length
                ? this.currentAssemblyOperations.map((operation) => {
                      return {
                          title: operation.OprSeq,
                          value: operation.OprSeq,
                      };
                  })
                : [];
        },
        jobPassDate() {
            if (this.job && this.job.CreateDate) {
                const created = moment(
                    this.job.CreateDate,
                    "YYYY-MM-DD HH:mm:ss"
                );
                const cutoff = moment("2026-02-09", "YYYY-MM-DD");
                return created.isSameOrAfter(cutoff, "day");
            }
            return false;
        },
    },
    data() {
        return {
            operationsArray: [],
            runningProcesses: {
                updatingOpCode: false,
                updatingQtyPer: false,
                updatingEstScrap: false,
                updatingEstScrapType: false,
                updatingOprSeq: false,
            },
            form: {
                AssemblySeq: null,
                Description: null,
                DueDate: null,
                QueStartDate: null,
                EstScrap: null,
                EstScrapType: null,
                IUM: null,
                JobComplete: null,
                JobEngineered: null,
                JobNum: null,
                OpCode: null,
                OpComplete: null,
                OprSeq: null,
                QtyPer: null,
                RevisionNum: null,
                RunQty: null,
                ProdQty: null,
                StartDate: null,
                SubContract: 0,
                MobileOperation: 0,
                ReWork: 0,
                SysRowID: null,
            },
            qtyPerCalculatorDialog: false,
        };
    },
    mounted() {
        if (this.currentOperation) {
            this.parseData(this.currentOperation);
        }
        this.fetchOperations();
    },
    watch: {
        currentOperation(value, oldValue) {
            this.fetchOperations();
            this.parseData(value);
        },
    },
    methods: {
        openQtyPerCalculator() {
            this.qtyPerCalculatorDialog = true;
        },
        closeQtyPerCalculator() {
            this.qtyPerCalculatorDialog = false;
        },
        qtyPerCalculatorReceived(val) {
            this.form.QtyPer = val;
            this.qtyPerCalculatorDialog = false;
            this.QtyPerChange();
        },
        async OpCodeChange() {
            if (!this.runningProcesses.updatingOpCode) {
                this.runningProcesses.updatingOpCode = true;

                let params = {
                    type: "OpCode",
                };

                await this.sendUpdateCall(params);

                this.runningProcesses.updatingOpCode = false;
            }
        },
        async QtyPerChange() {
            if (
                !this.runningProcesses.updatingQtyPer &&
                this.currentOperation.QtyPer !== this.form.QtyPer
            ) {
                this.runningProcesses.updatingQtyPer = true;

                let params = {
                    type: "QtyPer",
                    QtyPer: this.form.QtyPer,
                };

                await this.sendUpdateCall(params);

                this.runningProcesses.updatingQtyPer = false;
            }
        },
        async OprSeqChange() {
            if (
                !this.runningProcesses.updatingOprSeq &&
                this.currentOperation.OprSeq !== this.form.OprSeq
            ) {
                this.runningProcesses.updatingOprSeq = true;

                let params = {
                    type: "OprSeq",
                    OprSeq: this.form.OprSeq,
                };

                await this.sendUpdateCall(params);

                this.runningProcesses.updatingOprSeq = false;
            }
        },
        async EstScrapChange() {
            if (
                !this.runningProcesses.updatingEstScrap &&
                parseFloat(this.currentOperation.EstScrap) !==
                    parseFloat(this.form.EstScrap)
            ) {
                this.runningProcesses.updatingEstScrap = true;

                let params = {
                    type: "EstScrap",
                    EstScrap: this.form.EstScrap,
                    EstScrapType: this.form.EstScrapType,
                };

                await this.sendUpdateCall(params);

                this.runningProcesses.updatingEstScrap = false;
            }
        },
        async EstScrapTypeChange() {
            if (
                !this.runningProcesses.updatingEstScrapType &&
                this.currentOperation.EstScrapType !== this.form.EstScrapType
            ) {
                this.runningProcesses.updatingEstScrapType = true;

                let params = {
                    type: "EstScrapType",
                    EstScrap: this.form.EstScrap,
                    EstScrapType: this.form.EstScrapType,
                };

                await this.sendUpdateCall(params);

                this.runningProcesses.updatingEstScrapType = false;
            }
        },
        async sendUpdateCall(additionalParams) {
            if (
                this.currentOperation &&
                this.job.JobNum &&
                parseInt(this.job.JobClosed) === 0
            ) {
                this.overlay = true;
                let params = {
                    OpCode: this.form.OpCode,
                    OperationId: this.currentOperation.id,
                    OprSeq: this.currentOperation?.OprSeq,
                    AssemblySeq: this.currentOperation?.AssemblySeq,
                    JobNum: this.currentOperation.JobNum,
                };
                params = { ...params, ...additionalParams };

                await axios
                    .post(route("api.epicor.jobs.opr.update"), params)
                    .then(async (response) => {
                        if (response.data.Status === 0) {
                            this.setErrorMessage(response.data.Message);
                            this.refreshAssemblies("operation");
                        } else if (response.data.Status === 1) {
                            this.refreshAssemblies("operation");
                            this.setTemporaryMessage(
                                "Value has been updated and saved"
                            );
                        }
                    })
                    .catch((response) => {
                        console.log("Err: " + response);
                    });
            }
        },
        parseData(data) {
            if (data) {
                this.form = Object.assign({}, data);
                this.form.SubContract = data.SubContract === "1";
                this.form.ProdQty = (
                    this.currentAssembly.RequiredQty * data.QtyPer
                ).toFixed();
                this.form.RunQty = parseFloat(data.RunQty).toFixed();
                this.form.QtyPer = parseFloat(data.QtyPer).toFixed();
                this.form.EstScrap = parseFloat(data.EstScrap);

                if (data.DueDate) {
                    let dateObject = new Date(data.DueDate);
                    let month = (dateObject.getMonth() + 1)
                        .toString()
                        .padStart(2, "0"); // getMonth() is zero-based
                    let day = dateObject.getDate().toString().padStart(2, "0");
                    let year = dateObject.getFullYear();
                    this.form.DueDate = `${year}-${month}-${day}`;
                }

                if (this.form.EstScrapType == "%") {
                    this.form.ScrapTotal = Math.ceil(
                        (parseFloat(this.form.ProdQty) / 100) *
                            parseFloat(this.form.EstScrap)
                    ).toFixed();
                } else {
                    this.form.ScrapTotal = parseFloat(
                        this.form.EstScrap
                    ).toFixed();
                }
            }
        },
        nextOperation() {
            if (this.currentAssemblyOperations.length < 2) {
                return;
            }

            let key = this.findCurrentKey();
            key += 1;
            if (key > this.currentAssemblyOperations.length - 1) {
                key = 0;
            }
            this.setCurrentOperation(
                this.currentAssemblyOperations[key].OprSeq
            );
        },
        previousOperation() {
            if (this.currentAssemblyOperations.length < 2) {
                return;
            }

            let key = this.findCurrentKey();
            key -= 1;
            if (key < 0) {
                key = this.currentAssemblyOperations.length - 1;
            }
            this.setCurrentOperation(
                this.currentAssemblyOperations[key].OprSeq
            );
        },
        firstOperation() {
            this.setCurrentOperation(this.currentAssemblyOperations[0].OprSeq);
        },
        lastOperation() {
            this.setCurrentOperation(
                this.currentAssemblyOperations[
                    this.currentAssemblyOperations.length - 1
                ].OprSeq
            );
        },
        findCurrentKey() {
            let i = 0;
            for (const mtl of this.currentAssemblyOperations) {
                if (mtl.OprSeq === this.currentOperation.OprSeq) {
                    return i;
                }
                i += 1;
            }
        },
        async fetchOperations() {
            await axios
                .get(
                    route("legacy-api.engJobOperations", {
                        subcontract: this.currentOperation?.SubContract === "1",
                    })
                )
                .then((response) => {
                    this.operationsArray = response.data;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
    },
};
</script>

<style scoped></style>
